/**
 * Browser-safe logger for verify-ui.
 *
 * Runtime configuration is injected via window.__RUNTIME_CONFIG__ in production
 * (generated by docker-entrypoint.sh at container startup).
 * Falls back to import.meta.env for local development with Vite.
 */
import * as browserLoggerPackage from '@safepassage/logger/browser';
import { redactForBrowserLog } from './browserSafety';

const BrowserLoggerClass = browserLoggerPackage.BrowserLogger;
type BrowserLoggerInstance = InstanceType<typeof BrowserLoggerClass>;

// Type for runtime config injected by docker-entrypoint.sh
interface RuntimeConfig {
  LOG_TRANSPORT_ENABLED?: string;
  BROWSER_LOG_API_URL?: string;
  LOG_LEVEL?: string;
  LOG_TRANSPORT_MAX_BUFFER_SIZE?: string;
  LOG_TRANSPORT_FLUSH_INTERVAL_MS?: string;
  APP_VERSION?: string;
}

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig;
  }
}

// Get runtime config (production) or Vite env vars (development)
const runtimeConfig = (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) || {};
const viteEnv = import.meta.env;

// Helper to get config value with runtime config taking precedence
const getConfig = (runtimeKey: keyof RuntimeConfig, viteKey: string, defaultValue: string): string => {
  return runtimeConfig[runtimeKey] || viteEnv[viteKey] || defaultValue;
};

// Detect environment
const MODE = (() => {
  const viteMode = viteEnv.MODE;
  const nodeMode =
    typeof process !== 'undefined' && typeof process.env !== 'undefined'
      ? process.env.NODE_ENV
      : undefined;
  return (viteMode ?? nodeMode ?? 'development').toString();
})();

type LogDocument = Record<string, unknown>;
type LogInput = string | Error | LogDocument;

class BrowserSafeLogger {
  constructor(private readonly inner: BrowserLoggerInstance) {}

  debug(messageOrDocument: LogInput, document?: LogInput): void {
    this.log('debug', messageOrDocument, document);
  }

  info(messageOrDocument: LogInput, document?: LogInput): void {
    this.log('info', messageOrDocument, document);
  }

  warn(messageOrDocument: LogInput, document?: LogInput): void {
    this.log('warn', messageOrDocument, document);
  }

  error(messageOrDocument: LogInput, document?: LogInput): void {
    this.log('error', messageOrDocument, document);
  }

  trace(messageOrDocument: LogInput, document?: LogInput): void {
    this.log('trace', messageOrDocument, document);
  }

  flush(): void {
    this.inner.flush();
  }

  clone(additionalDefaults?: LogDocument): BrowserSafeLogger {
    const safeDefaults = redactForBrowserLog(additionalDefaults || {});
    const defaults = isRecord(safeDefaults) ? safeDefaults : {};
    return new BrowserSafeLogger(this.inner.clone(defaults));
  }

  private log(
    level: 'debug' | 'info' | 'warn' | 'error' | 'trace',
    messageOrDocument: LogInput,
    document?: LogInput
  ): void {
    if (messageOrDocument instanceof Error) {
      const safeError = redactForBrowserLog(messageOrDocument);
      this.inner[level]('Browser error', isRecord(safeError) ? safeError : {});
      return;
    }

    if (typeof messageOrDocument === 'string') {
      const safeMessage = redactForBrowserLog(messageOrDocument);
      const safeDocument = redactForBrowserLog(document || {});
      this.inner[level](
        typeof safeMessage === 'string' ? safeMessage : 'Browser log',
        isRecord(safeDocument) ? safeDocument : {}
      );
      return;
    }

    const safeDocument = redactForBrowserLog(messageOrDocument);
    const fields = isRecord(safeDocument) ? safeDocument : {};
    const message = typeof fields.message === 'string'
      ? fields.message
      : 'Structured browser log event';
    this.inner[level](message, fields);
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// Create the browser logger for verify-ui.
const logTransportEnabled = getConfig('LOG_TRANSPORT_ENABLED', 'VITE_LOG_TRANSPORT_ENABLED', 'false') === 'true';
const configuredLogUrl = getConfig('BROWSER_LOG_API_URL', 'VITE_BROWSER_LOG_API_URL', '');
const browserLogUrl = configuredLogUrl || inferOwnedLogEndpoint();

const baseLogger = new BrowserLoggerClass({
  endpoint: browserLogUrl,
  serviceName: 'verify-ui',
  enabled: logTransportEnabled,
  development: MODE !== 'production',
  maxBufferSize: parseInt(getConfig('LOG_TRANSPORT_MAX_BUFFER_SIZE', 'VITE_LOG_TRANSPORT_MAX_BUFFER_SIZE', '500'), 10),
  flushIntervalMs: parseInt(getConfig('LOG_TRANSPORT_FLUSH_INTERVAL_MS', 'VITE_LOG_TRANSPORT_FLUSH_INTERVAL_MS', '2000'), 10),
});
baseLogger.setDefaultContext({
  environment: MODE,
  appVersion: getConfig('APP_VERSION', 'VITE_APP_VERSION', '0.0.0'),
  loggerImplementation: 'safepassage-browser',
});

export const logger = new BrowserSafeLogger(baseLogger);

// Configure transport if enabled via runtime config or environment variables
export { BrowserLoggerClass as Logger };
export type { BrowserSafeLogger };

// Helper to create child logger with transport options inherited
const createChildLogger = (labels: Record<string, string>): BrowserSafeLogger => {
  const child = logger.clone({ labels });
  return child;
};

export function inferOwnedLogEndpoint(
  locationOverride?: Pick<Location, 'hostname' | 'protocol'>,
): string {
  if (typeof window === 'undefined' && !locationOverride) {
    return '';
  }
  const location = locationOverride || window.location;
  const ownedApiOrigins: Record<string, string> = {
    'av.safepassageapp.com': 'https://api.safepassageapp.com',
    'verify.staging.safepassageapp.com':
      'https://api.staging.safepassageapp.com',
    'verify.privateav.com': 'https://api.privateav.com',
    'verify.staging.privateav.com': 'https://api.staging.privateav.com',
    localhost: 'http://localhost:3001',
    '127.0.0.1': 'http://localhost:3001',
  };
  const apiOrigin = ownedApiOrigins[location.hostname.toLowerCase()];
  return apiOrigin ? `${apiOrigin}/api/v1/logs` : '';
}

// Component-specific child loggers (using clone with labels)
export const cameraLogger = createChildLogger({ component: 'Camera' });

export const wsLogger = createChildLogger({ component: 'WebSocket' });

export const verificationLogger = createChildLogger({ component: 'Verification' });

export const navigationLogger = createChildLogger({ component: 'Navigation' });
